import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, LexicalCommand, NodeKey, SerializedLexicalNode, Spread } from 'lexical'

import { useCallback, useRef, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import OpenWithIcon from '@mui/icons-material/OpenWith'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getNodeByKey, DecoratorNode, createCommand } from 'lexical'

export type InsertImagePayload = {
  altText: string
  height?: number
  src: string
  width?: number
}

export type SerializedImageNode = Spread<
  {
    altText: string
    height?: number
    src: string
    type: 'image'
    version: 1
    width?: number
  },
  SerializedLexicalNode
>

type ResizeDirection = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw'

type ResizeState = {
  direction: ResizeDirection
  startX: number
  startY: number
  startWidth: number
  startHeight: number
}

const MIN_IMAGE_SIZE = 80

function parseDimension(value: string | null) {
  if (!value) {
    return undefined
  }

  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function clampDimension(value: number, max: number) {
  return Math.max(MIN_IMAGE_SIZE, Math.min(Math.round(value), max))
}

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (!(domNode instanceof HTMLImageElement)) {
    return null
  }

  return {
    node: $createImageNode({
      altText: domNode.alt,
      height: parseDimension(domNode.getAttribute('height')) || parseDimension(domNode.style.height),
      src: domNode.src,
      width: parseDimension(domNode.getAttribute('width')) || parseDimension(domNode.style.width),
    }),
  }
}

function ImageComponent({ altText, height, nodeKey, src, width }: InsertImagePayload & { nodeKey: NodeKey }) {
  const [editor] = useLexicalComposerContext()
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const resizeStateRef = useRef<ResizeState | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [draftSize, setDraftSize] = useState({ height, width })

  const activeWidth = draftSize.width || width
  const activeHeight = draftSize.height || height

  const getMaxWidth = useCallback(() => {
    const editorInput = wrapperRef.current?.closest<HTMLElement>('.editor-input')
    return Math.max(MIN_IMAGE_SIZE, (editorInput?.clientWidth || 720) - 8)
  }, [])

  const beginEdit = useCallback(() => {
    const bounds = imageRef.current?.getBoundingClientRect()
    setDraftSize({
      height: height || Math.round(bounds?.height || MIN_IMAGE_SIZE),
      width: width || Math.round(bounds?.width || MIN_IMAGE_SIZE),
    })
    setIsEditing(true)
  }, [height, width])

  const cancelEdit = useCallback(() => {
    setDraftSize({ height, width })
    setIsEditing(false)
  }, [height, width])

  const saveEdit = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey)
      if ($isImageNode(node)) {
        node.setDimensions(draftSize.width, draftSize.height)
      }
    })
    setIsEditing(false)
  }, [draftSize.height, draftSize.width, editor, nodeKey])

  const startResize = useCallback((event: React.PointerEvent<HTMLButtonElement>, direction: ResizeDirection) => {
    event.preventDefault()
    event.stopPropagation()

    const bounds = imageRef.current?.getBoundingClientRect()
    if (!bounds) {
      return
    }

    resizeStateRef.current = {
      direction,
      startHeight: draftSize.height || Math.round(bounds.height),
      startWidth: draftSize.width || Math.round(bounds.width),
      startX: event.clientX,
      startY: event.clientY,
    }

    event.currentTarget.setPointerCapture(event.pointerId)
  }, [draftSize.height, draftSize.width])

  const resize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const state = resizeStateRef.current
    if (!state) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const deltaX = event.clientX - state.startX
    const deltaY = event.clientY - state.startY
    const maxWidth = getMaxWidth()
    const maxHeight = Math.max(MIN_IMAGE_SIZE, Math.round(maxWidth * 3))
    let nextWidth = state.startWidth
    let nextHeight = state.startHeight

    if (state.direction.includes('e')) {
      nextWidth = state.startWidth + deltaX
    }
    if (state.direction.includes('w')) {
      nextWidth = state.startWidth - deltaX
    }
    if (state.direction.includes('s')) {
      nextHeight = state.startHeight + deltaY
    }
    if (state.direction.includes('n')) {
      nextHeight = state.startHeight - deltaY
    }

    setDraftSize({
      height: clampDimension(nextHeight, maxHeight),
      width: clampDimension(nextWidth, maxWidth),
    })
  }, [getMaxWidth])

  const stopResize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    resizeStateRef.current = null
  }, [])

  const handles: { direction: ResizeDirection; sx: Record<string, string | number> }[] = [
    { direction: 'nw', sx: { cursor: 'nwse-resize', left: -5, top: -5 } },
    { direction: 'n', sx: { cursor: 'ns-resize', left: '50%', top: -5, transform: 'translateX(-50%)' } },
    { direction: 'ne', sx: { cursor: 'nesw-resize', right: -5, top: -5 } },
    { direction: 'e', sx: { cursor: 'ew-resize', right: -5, top: '50%', transform: 'translateY(-50%)' } },
    { direction: 'se', sx: { bottom: -5, cursor: 'nwse-resize', right: -5 } },
    { direction: 's', sx: { bottom: -5, cursor: 'ns-resize', left: '50%', transform: 'translateX(-50%)' } },
    { direction: 'sw', sx: { bottom: -5, cursor: 'nesw-resize', left: -5 } },
    { direction: 'w', sx: { cursor: 'ew-resize', left: -5, top: '50%', transform: 'translateY(-50%)' } },
  ]

  return (
    <Box
      ref={wrapperRef}
      contentEditable={false}
      sx={{
        display: 'block',
        maxWidth: '100%',
        my: 1.5,
        position: 'relative',
        width: activeWidth ? `${activeWidth}px` : 'fit-content',
        '&:hover .image-resize-button': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      <Box
        component="img"
        ref={imageRef}
        alt={altText}
        src={src}
        sx={{
          borderRadius: 1.5,
          display: 'block',
          height: activeHeight ? `${activeHeight}px` : 'auto',
          maxWidth: '100%',
          objectFit: 'fill',
          width: activeWidth ? `${activeWidth}px` : 'auto',
        }}
      />
      {!isEditing && (
        <Tooltip title="Resize image">
          <IconButton
            className="image-resize-button"
            size="small"
            type="button"
            onClick={beginEdit}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.2)',
              opacity: 0,
              pointerEvents: 'none',
              position: 'absolute',
              right: 8,
              top: 8,
              transition: 'opacity 120ms ease',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <OpenWithIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isEditing && (
        <>
          <Box
            sx={{
              border: '2px solid #0f4c81',
              inset: -2,
              pointerEvents: 'none',
              position: 'absolute',
            }}
          />
          {handles.map((handle) => (
            <Box
              component="button"
              type="button"
              key={handle.direction}
              onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => startResize(event, handle.direction)}
              onPointerMove={resize}
              onPointerUp={stopResize}
              onPointerCancel={stopResize}
              aria-label={`Resize image ${handle.direction}`}
              sx={{
                bgcolor: '#fff',
                border: '2px solid #0f4c81',
                borderRadius: '50%',
                height: 12,
                p: 0,
                position: 'absolute',
                touchAction: 'none',
                width: 12,
                ...handle.sx,
              }}
            />
          ))}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
              p: 0.5,
              position: 'absolute',
              right: 0,
              top: -42,
            }}
          >
            <Tooltip title="Save image size">
              <IconButton size="small" color="primary" type="button" onClick={saveEdit}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel image resize">
              <IconButton size="small" type="button" onClick={cancelEdit}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      )}
    </Box>
  )
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string
  __altText: string
  __width?: number
  __height?: number

  static getType() {
    return 'image'
  }

  static clone(node: ImageNode) {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key)
  }

  static importJSON(serializedNode: SerializedImageNode) {
    return $createImageNode({
      altText: serializedNode.altText,
      height: serializedNode.height,
      src: serializedNode.src,
      width: serializedNode.width,
    })
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 1,
      }),
    }
  }

  constructor(src: string, altText = '', width?: number, height?: number, key?: NodeKey) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__width = width
    this.__height = height
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img')
    element.setAttribute('src', this.__src)
    element.setAttribute('alt', this.__altText)
    if (this.__width) {
      element.setAttribute('width', String(this.__width))
    }
    if (this.__height) {
      element.setAttribute('height', String(this.__height))
    }
    return { element }
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.__altText,
      height: this.__height,
      src: this.__src,
      type: 'image',
      version: 1,
      width: this.__width,
    }
  }

  setDimensions(width?: number, height?: number) {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
    return writable
  }

  createDOM() {
    return document.createElement('span')
  }

  updateDOM() {
    return false
  }

  decorate() {
    return <ImageComponent altText={this.__altText} height={this.__height} nodeKey={this.__key} src={this.__src} width={this.__width} />
  }
}

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand()

export function $createImageNode({ altText, height, src, width }: InsertImagePayload) {
  return new ImageNode(src, altText, width, height)
}

export function $isImageNode(node: unknown): node is ImageNode {
  return node instanceof ImageNode
}
